/**
 * Scoring - Density and T-Junction Scoring System
 * 
 * Features:
 * - Grid sampling density calculation (intuitive "percentage of space filled")
 * - Improved T-Junction detection (compares top edges, not centers)
 * - Callbacks for celebration effects
 */

import { PhysicsWorld } from '../engine/PhysicsWorld';
import { Container, BodyBounds } from './Container';
import { SCORING } from './Constants';

/** T-Junction detection result */
export interface TJunctionResult {
    /** Unique ID for this T-junction (for deduplication) */
    id: string;
    /** The bridge body handle */
    bridgeHandle: number;
    /** Left base body handle */
    leftBaseHandle: number;
    /** Right base body handle */
    rightBaseHandle: number;
    /** X position of the seam */
    seamX: number;
    /** Y position for celebration (top of bases) */
    celebrationY: number;
}

/** Scoring state snapshot */
export interface ScoringState {
    density: number;           // 0-100 percentage
    tJunctionCount: number;
    massScore: number;
    bonusScore: number;
    totalScore: number;
}

/** T-Junction callback type */
export type TJunctionCallback = (result: TJunctionResult) => void;

/**
 * Scoring system class
 */
export class Scoring {
    private physics: PhysicsWorld;
    private container: Container;
    
    // Grid sampling configuration
    private gridCellSize = 20;  // pixels per cell (will be set from layout)
    
    // T-Junction detection
    private detectedTJunctions: Set<string> = new Set();
    private tJunctionCount = 0;
    private tJunctionCallbacks: TJunctionCallback[] = [];
    
    // Current score state
    private density = 0;
    private totalPackageArea = 0;

    constructor(physics: PhysicsWorld, container: Container) {
        this.physics = physics;
        this.container = container;
    }

    /**
     * Set grid cell size from layout dimensions (for responsive scaling)
     */
    setGridCellSize(size: number): void {
        this.gridCellSize = Math.max(size, 10); // Minimum 10px for performance
    }

    /**
     * Register callback for T-Junction detection
     */
    onTJunction(callback: TJunctionCallback): void {
        this.tJunctionCallbacks.push(callback);
    }

    /**
     * Clear T-junction tracking (for new game)
     */
    reset(): void {
        this.detectedTJunctions.clear();
        this.tJunctionCount = 0;
        this.density = 0;
        this.totalPackageArea = 0;
    }

    /**
     * Update scoring calculations.
     * Should be called periodically (every 10 frames during play, 30 during settling).
     * 
     * @param tJunctionBonus Points per T-junction for this difficulty
     */
    update(tJunctionBonus: number): void {
        this.calculateDensity();
        this.detectTJunctions(tJunctionBonus);
    }

    /**
     * Get current scoring state
     */
    getState(tJunctionBonus: number): ScoringState {
        const densityFactor = this.density / 100;
        const massScore = this.totalPackageArea * 0.1 * densityFactor;
        const bonusScore = this.tJunctionCount * tJunctionBonus;
        
        return {
            density: this.density,
            tJunctionCount: this.tJunctionCount,
            massScore,
            bonusScore,
            totalScore: massScore + bonusScore,
        };
    }

    /**
     * Get just the density value
     */
    getDensity(): number {
        return this.density;
    }

    /**
     * Get T-junction count
     */
    getTJunctionCount(): number {
        return this.tJunctionCount;
    }

    // ==================== DENSITY CALCULATION ====================

    /**
     * Calculate density using grid sampling.
     * 
     * Grid sampling method:
     * 1. Create a grid of cells over the container
     * 2. For each cell, check if any package overlaps it
     * 3. Density = filled cells / total cells up to stack height
     * 
     * This is intuitive ("what percentage of space is actually filled")
     * and handles irregular shapes naturally.
     */
    private calculateDensity(): void {
        const geometry = this.container.getGeometry();
        if (!geometry) {
            this.density = 0;
            return;
        }

        const bounds = this.container.getContainerBodyBounds();
        if (bounds.length === 0) {
            this.density = 0;
            this.totalPackageArea = 0;
            return;
        }

        // Calculate total package area
        this.totalPackageArea = 0;
        for (const b of bounds) {
            this.totalPackageArea += b.area;
        }

        // Find the top of the stack (lowest Y value among all packages)
        let stackTopY = geometry.floorY;
        for (const b of bounds) {
            if (b.minY < stackTopY) {
                stackTopY = b.minY;
            }
        }

        // Clamp stack top to container top at minimum
        stackTopY = Math.max(stackTopY, geometry.top);

        // Calculate grid dimensions
        const gridWidth = Math.ceil(geometry.width / this.gridCellSize);
        const stackHeight = geometry.floorY - stackTopY;
        const gridHeight = Math.ceil(stackHeight / this.gridCellSize);

        if (gridWidth <= 0 || gridHeight <= 0) {
            this.density = 0;
            return;
        }

        // Count filled cells
        let filledCells = 0;
        const totalCells = gridWidth * gridHeight;

        for (let gx = 0; gx < gridWidth; gx++) {
            for (let gy = 0; gy < gridHeight; gy++) {
                // Calculate cell center position
                const cellCenterX = geometry.left + (gx + 0.5) * this.gridCellSize;
                const cellCenterY = stackTopY + (gy + 0.5) * this.gridCellSize;

                // Check if any package overlaps this cell
                if (this.isCellFilled(cellCenterX, cellCenterY, bounds)) {
                    filledCells++;
                }
            }
        }

        // Calculate density percentage
        this.density = Math.min(100, (filledCells / totalCells) * 100);
    }

    /**
     * Check if a grid cell overlaps any package.
     * Uses a simple center-point check for speed.
     */
    private isCellFilled(cx: number, cy: number, bounds: BodyBounds[]): boolean {
        const halfCell = this.gridCellSize / 2;

        for (const b of bounds) {
            // Check if cell overlaps with package AABB
            // Cell bounds: [cx - halfCell, cx + halfCell] x [cy - halfCell, cy + halfCell]
            // Package bounds: [b.minX, b.maxX] x [b.minY, b.maxY]
            
            const overlapX = cx - halfCell < b.maxX && cx + halfCell > b.minX;
            const overlapY = cy - halfCell < b.maxY && cy + halfCell > b.minY;
            
            if (overlapX && overlapY) {
                return true;
            }
        }

        return false;
    }

    // ==================== T-JUNCTION DETECTION ====================

    /**
     * Detect T-Junctions in the current package arrangement.
     * 
     * Improved algorithm:
     * - Compares TOP EDGES of bases (not centers) for proper alignment
     * - Handles packages of different sizes correctly
     */
    private detectTJunctions(_tJunctionBonus: number): void {
        const bounds = this.container.getContainerBodyBounds();
        if (bounds.length < 3) return;

        // Filter to settled bodies only
        const settledBounds = bounds.filter(b => this.isSettled(b.body.handle));
        if (settledBounds.length < 3) return;

        // Check each potential bridge
        for (const bridge of settledBounds) {
            // For bridges, we're more permissive - triangles can be bridges too
            // Bases still need to be roughly horizontal for stability
            if (!this.canBeBridge(bridge)) continue;

            // Find all bodies this bridge is resting on
            const touchingBases = settledBounds.filter(base => 
                base.body.handle !== bridge.body.handle &&
                this.isRoughlyHorizontal(base.body.handle) &&
                this.isBridgeRestingOn(bridge, base)
            );

            // Check all pairs of touching bases
            for (let i = 0; i < touchingBases.length; i++) {
                for (let j = i + 1; j < touchingBases.length; j++) {
                    const baseA = touchingBases[i];
                    const baseB = touchingBases[j];
                    
                    if (baseA && baseB) {
                        this.checkTJunction(bridge, baseA, baseB);
                    }
                }
            }
        }
    }

    /**
     * Check if a body can act as a bridge piece.
     * Boxes and bags need to be roughly horizontal.
     * Triangles are always allowed since they create interesting formations.
     */
    private canBeBridge(bounds: BodyBounds): boolean {
        const shapeType = bounds.body.userData.shapeType;
        
        // Triangles can always be bridges - they create fun T-junctions
        if (shapeType === 'triangle') {
            return true;
        }
        
        // Boxes and bags need to be roughly horizontal
        return this.isRoughlyHorizontal(bounds.body.handle);
    }

    /**
     * Check if a body is settled (low velocity)
     */
    private isSettled(handle: number): boolean {
        const vel = this.physics.getVelocity(handle);
        if (!vel) return false;

        const speed = Math.sqrt(vel.x * vel.x + vel.y * vel.y);
        return speed < SCORING.settledVelocityThreshold;
    }

    /**
     * Check if a body is roughly horizontal (flat orientation)
     */
    private isRoughlyHorizontal(handle: number): boolean {
        const angle = this.physics.getRotation(handle);
        if (angle === null) return false;

        // Normalize angle to -PI to PI
        let normalized = angle % (Math.PI * 2);
        if (normalized > Math.PI) normalized -= Math.PI * 2;
        if (normalized < -Math.PI) normalized += Math.PI * 2;

        const tolerance = Math.PI / 8;  // ~22.5 degrees
        const absAngle = Math.abs(normalized);

        // Check if close to 0°, 90°, or 180°
        return (
            absAngle < tolerance ||
            Math.abs(absAngle - Math.PI / 2) < tolerance ||
            Math.abs(absAngle - Math.PI) < tolerance
        );
    }

    /**
     * Check if bridge is resting on top of a base
     */
    private isBridgeRestingOn(bridge: BodyBounds, base: BodyBounds): boolean {
        // Bridge bottom should be close to base top
        const bridgeBottom = bridge.maxY;
        const baseTop = base.minY;
        const gap = baseTop - bridgeBottom;

        // Allow small tolerance for physics settling
        const maxGap = 15;
        const minGap = -20;  // Allow slight overlap
        if (gap > maxGap || gap < minGap) return false;

        // Check horizontal overlap
        const overlapLeft = Math.max(bridge.minX, base.minX);
        const overlapRight = Math.min(bridge.maxX, base.maxX);
        const overlap = overlapRight - overlapLeft;

        // Require meaningful overlap
        const minWidth = Math.min(bridge.width, base.width);
        const minOverlap = Math.min(15, minWidth * 0.15);

        return overlap >= minOverlap;
    }

    /**
     * Validate and score a potential T-junction.
     * KEY IMPROVEMENT: Compares TOP EDGES, not centers.
     */
    private checkTJunction(
        bridge: BodyBounds,
        baseA: BodyBounds,
        baseB: BodyBounds
    ): void {
        // Create unique ID for deduplication
        const ids = [bridge.body.handle, baseA.body.handle, baseB.body.handle]
            .sort((a, b) => a - b)
            .join('-');
        
        if (this.detectedTJunctions.has(ids)) return;

        // Determine left and right base
        const leftBase = baseA.x < baseB.x ? baseA : baseB;
        const rightBase = baseA.x < baseB.x ? baseB : baseA;

        // 1. CHECK TOP EDGES ARE ALIGNED (not centers!)
        // This is the key improvement - different sized packages with aligned tops now work
        const topEdgeDiff = Math.abs(leftBase.minY - rightBase.minY);
        const maxTopDiff = 20;  // Tolerance for top edge alignment
        if (topEdgeDiff > maxTopDiff) return;

        // 2. Bases must be adjacent (small gap between them)
        const baseGap = rightBase.minX - leftBase.maxX;
        const maxBaseGap = 30;  // Maximum gap between bases
        const minBaseGap = -25; // Allow slight overlap
        if (baseGap > maxBaseGap || baseGap < minBaseGap) return;

        // 3. Find the seam point
        const seamX = (leftBase.maxX + rightBase.minX) / 2;

        // 4. Bridge must span across the seam
        const bridgeLeft = bridge.minX;
        const bridgeRight = bridge.maxX;
        
        const leftExtension = seamX - bridgeLeft;
        const rightExtension = bridgeRight - seamX;
        
        const minExtension = 15;
        if (leftExtension < minExtension || rightExtension < minExtension) return;

        // 5. Bridge center should be reasonably near the seam
        const bridgeWidth = bridgeRight - bridgeLeft;
        if (Math.abs(bridge.x - seamX) > bridgeWidth * 0.6) return;

        // Valid T-Junction!
        this.detectedTJunctions.add(ids);
        this.tJunctionCount++;

        // Create result for callbacks
        const celebrationY = Math.min(leftBase.minY, rightBase.minY);
        const result: TJunctionResult = {
            id: ids,
            bridgeHandle: bridge.body.handle,
            leftBaseHandle: leftBase.body.handle,
            rightBaseHandle: rightBase.body.handle,
            seamX,
            celebrationY,
        };

        // Notify listeners
        for (const callback of this.tJunctionCallbacks) {
            callback(result);
        }
    }
}
